import {
  ArrowDownRight,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  Clock,
  UserCheck,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { FC } from "react";

interface StatCardsProps {
  readonly totalOrgs: number;
  readonly activeOrgs: number;
  readonly growthRate: number;
  readonly activeToday: number;
  readonly activeThisWeek: number;
  readonly responsiblePersonCount: number;
}

/**
 * Displays key statistics in card format. Pure functional component with no side effects.
 */
export const StatCards: FC<StatCardsProps> = ({
  totalOrgs,
  activeOrgs,
  growthRate,
  activeToday,
  activeThisWeek,
  responsiblePersonCount,
}) => {
  const isGrowthPositive = growthRate >= 0;
  const growthRateAbs = Math.abs(growthRate).toFixed(1);

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Total Organizations
          </CardTitle>
          <Building2 className="text-muted-foreground h-4 w-4" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalOrgs}</div>
          <div className="text-muted-foreground flex items-center text-xs">
            <span
              className={`flex items-center ${isGrowthPositive ? "text-green-500" : "text-red-500"}`}
            >
              {isGrowthPositive ? (
                <ArrowUpRight className="h-4 w-4" />
              ) : (
                <ArrowDownRight className="h-4 w-4" />
              )}
              {growthRateAbs}% MoM
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Active Organizations
          </CardTitle>
          <CheckCircle2 className="text-muted-foreground h-4 w-4" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{activeOrgs}</div>
          <p className="text-muted-foreground text-xs">
            {((activeOrgs / totalOrgs) * 100).toFixed(1)}% of total
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Today</CardTitle>
          <Clock className="text-muted-foreground h-4 w-4" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{activeToday}</div>
          <p className="text-muted-foreground text-xs">
            {activeThisWeek} active this week
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Avg. Organizations per RP
          </CardTitle>
          <UserCheck className="text-muted-foreground h-4 w-4" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {responsiblePersonCount === 0
              ? 0
              : (totalOrgs / responsiblePersonCount).toFixed(1)}
          </div>
          <p className="text-muted-foreground text-xs">
            {responsiblePersonCount} total RPs
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
